// Re-export type
export type { BlogPost } from "./blog/types";

// Import all posts
import { BlogPost } from "./blog/types";
import { comoComprimirSinPerderCalidad } from "./blog/como-comprimir-imagenes-sin-perder-calidad";
import { jpgVsPngVsWebp } from "./blog/jpg-vs-png-vs-webp";
import { optimizarImagenesParaWeb } from "./blog/optimizar-imagenes-para-web";
import { comoComprimirParaWeb } from "./blog/como-comprimir-imagenes-para-web";
import { comoComprimirImagenA100kb } from "./blog/como-comprimir-imagen-a-100kb";
import { comoReducirImagenParaWhatsapp } from "./blog/como-reducir-imagen-para-whatsapp";
import { comoReducirImagenParaFormulario } from "./blog/como-reducir-imagen-para-formulario";
import { comprimirImagenSinPerderCalidad } from "./blog/comprimir-imagen-sin-perder-calidad";
import { comprimirImagenA200kb } from "./blog/comprimir-imagen-a-200kb";
import { comoEnviarImagenWhatsappSinPerderCalidad } from "./blog/como-enviar-imagen-whatsapp-sin-perder-calidad";
import { convertirPngAJpg } from "./blog/convertir-png-a-jpg";
import { reducirImagenParaEmail } from "./blog/reducir-imagen-para-email";
import { comprimirImagenA50kb } from "./blog/comprimir-imagen-a-50kb";
import { reducirImagenA300kb } from "./blog/como-reducir-imagen-a-300kb";
import { comprimirImagenA20kb } from "./blog/como-comprimir-imagen-a-20kb";
import { reducirImagenParaDni } from "./blog/como-reducir-imagen-para-dni";
import { comprimirFotoParaCv } from "./blog/como-comprimir-foto-para-cv";
import { comoReducirImagenParaEmail } from "./blog/como-reducir-imagen-para-email";
import { reducirImagenParaInstagram } from "./blog/como-reducir-imagen-para-instagram";
import { mejorarImagenBorrosa } from "./blog/como-mejorar-imagen-borrosa-online";
import { aclararImagenBorrosa } from "./blog/como-aclarar-imagen-borrosa";
import { arreglarFotosBorrosas } from "./blog/arreglar-fotos-borrosas-gratis";
import { arreglarImagenPixelada } from "./blog/como-arreglar-imagen-pixelada";

export const blogPosts: BlogPost[] = [
  arreglarImagenPixelada,
  arreglarFotosBorrosas,
  aclararImagenBorrosa,
  mejorarImagenBorrosa,
  reducirImagenParaInstagram,
  comoReducirImagenParaEmail,
  comprimirFotoParaCv,
  reducirImagenParaDni,
  comprimirImagenA20kb,
  reducirImagenA300kb,
  comprimirImagenA50kb,
  reducirImagenParaEmail,
  convertirPngAJpg,
  comoEnviarImagenWhatsappSinPerderCalidad,
  comprimirImagenA200kb,
  comprimirImagenSinPerderCalidad,
  comoReducirImagenParaFormulario,
  comoComprimirImagenA100kb,
  comoReducirImagenParaWhatsapp,
  comoComprimirSinPerderCalidad,
  jpgVsPngVsWebp,
  optimizarImagenesParaWeb,
  comoComprimirParaWeb,
  {
    slug: "diferencia-entre-jpg-png-webp",
    title: "Diferencia entre JPG, PNG y WebP: guía técnica para elegir el formato correcto",
    description: "Comparativa técnica entre JPEG, PNG y WebP. Cuándo usar cada formato, diferencias de tamaño y calidad, compatibilidad, y casos prácticos con ejemplos reales.",
    date: "2026-02-10",
    readTime: "8 min",
    keywords: "diferencia jpg png webp, jpg vs png, webp vs jpg, formatos imagen web, cuando usar png, cuando usar jpg",
    content: `
## La decisión que más afecta al peso de tus imágenes

El formato que eliges para guardar una imagen puede significar la diferencia entre un archivo de 50KB y uno de 2MB — con exactamente la misma imagen. Elegir mal el formato es uno de los errores más comunes en optimización web, y también uno de los más fáciles de corregir.

Esta guía explica cómo funciona cada formato técnicamente, para qué tipo de imagen está diseñado, y qué criterios usar para decidir.

## Tabla comparativa

| Característica | JPEG | PNG | WebP |
|---|---|---|---|
| Tipo de compresión | Con pérdida (lossy) | Sin pérdida (lossless) | Ambas opciones |
| Transparencia | No | Sí (canal alfa) | Sí |
| Animación | No | No (APNG sí) | Sí |
| Tamaño para fotos | Pequeño | Muy grande | Muy pequeño |
| Tamaño para gráficos | Mediocre | Pequeño-mediano | Pequeño |
| Soporte navegadores | Universal | Universal | 97%+ (todos modernos) |
| Soporte apps de edición | Universal | Universal | Bueno, no universal |
| Cuándo usarlo | Fotos, imágenes con gradientes | Logos, capturas, transparencias | Todo uso web moderno |

## JPEG: compresión inteligente para fotografías

JPEG (Joint Photographic Experts Group, 1992) fue diseñado específicamente para fotografías. El algoritmo funciona dividiendo la imagen en bloques de 8×8 píxeles y aplicando una transformada matemática (DCT — Discrete Cosine Transform) que descarta la información de alta frecuencia que el ojo humano percibe con menos precisión.

### Cómo funciona la calidad JPEG

La "calidad" JPEG (0-100%) controla cuánta información de alta frecuencia se descarta. A calidad 85%, el resultado es prácticamente indistinguible del original en condiciones normales de uso. A calidad 70%, empiezan a aparecer artefactos visibles en áreas uniformes como cielos y fondos. A calidad 50%, los bloques de 8×8 píxeles se hacen visibles claramente.

**El punto óptimo para uso web:** 80-90% de calidad. A ese rango:
- Una foto de 3MB se convierte en 150-400KB
- La diferencia visual con el original es imperceptible en pantalla
- El tamaño es manejable para web y email

### Para qué es bueno JPEG

- Fotografías de personas, paisajes, productos
- Imágenes con gran variación de color y gradientes suaves
- Cualquier contenido donde necesites compatibilidad máxima
- Imágenes que se van a ver en pantalla (no imprimir en alta resolución)

### Para qué es malo JPEG

- **Logos y texto:** JPEG crea artefactos visibles alrededor de los bordes duros del texto y las esquinas definidas de logos. Los bordes de letras quedan borrosos.
- **Capturas de pantalla:** El texto de una interfaz se degrada notablemente incluso al 90%.
- **Imágenes que vas a editar varias veces:** Cada vez que guardas como JPEG se aplica una nueva ronda de compresión. La degradación es acumulativa.
- **Transparencia:** JPEG no soporta canal alfa. Las áreas transparentes se rellenan con el color de fondo (normalmente blanco).

### Tamaños típicos de JPEG

Para una fotografía de 1000×1000 px con contenido fotográfico típico:
- JPEG 95%: ~400KB
- JPEG 85%: ~180KB
- JPEG 75%: ~120KB
- JPEG 60%: ~80KB

## PNG: calidad perfecta cuando la transparencia importa

PNG (Portable Network Graphics, 1996) usa compresión sin pérdida: el archivo comprimido es matemáticamente idéntico al original, cada pixel exactamente igual. El algoritmo (DEFLATE, el mismo que ZIP) busca patrones repetidos en los datos y los codifica de forma más compacta.

### Tipos de PNG

**PNG-24** (24 bits por pixel, ~16 millones de colores): El formato PNG estándar para fotografías y gráficos con gradientes. Calidad perfecta pero archivos grandes — en general no tiene sentido usar PNG-24 para fotografías frente a JPEG o WebP.

**PNG-8** (8 bits, hasta 256 colores): Usa una paleta de colores limitada. Para logos e iconos con pocos colores, PNG-8 puede ser mucho más pequeño que PNG-24 sin diferencia visual. Herramientas como Photoshop o pngquant pueden convertir a PNG-8.

**PNG con transparencia** (canal alfa): La razón principal para usar PNG en web moderna. El canal alfa permite transparencia total, parcial, o gradientes de transparencia — algo que JPEG no puede hacer.

### Cuándo usar PNG

- **Logos con fondo transparente:** La aplicación más común. Si tu logo necesita funcionar sobre fondos de diferentes colores, PNG es la opción correcta (o WebP para web moderna).
- **Capturas de pantalla con texto:** El texto nítido en capturas no sufre degradación con PNG lossless.
- **Iconos e ilustraciones vectoriales rasterizadas:** Gráficos con bordes definidos y áreas de color uniforme se comprimen bien con PNG-8.
- **Imágenes de trabajo intermedio:** Si vas a editar la imagen varias veces, guarda los intermedios como PNG para no acumular pérdida de calidad.

### Para qué PNG es una mala elección

- **Fotografías para web:** Un retrato de 1000×1000 px puede pesar 2-3MB como PNG-24 frente a 180KB como JPEG 85%. La diferencia visual en pantalla es imperceptible.
- **Imágenes en emails masivos:** PNG suele pesar más y algunos clientes de email lo procesan peor.

## WebP: el sucesor diseñado para la web moderna

WebP (desarrollado por Google, lanzado 2010) fue creado específicamente como sustituto de JPEG y PNG para uso web. Ofrece ambos modos: lossy (para fotos, como JPEG pero más eficiente) y lossless (para gráficos, como PNG pero más pequeño).

### Por qué WebP es más eficiente

WebP lossy usa una técnica de predicción de bloques más sofisticada que JPEG, lo que permite representar la misma información visual con menos datos. WebP lossless usa un método diferente a PNG para encontrar redundancias, resultando en archivos típicamente 20-30% más pequeños.

**Comparación directa** para una fotografía típica de 1000×1000 px:
- JPEG 85%: ~180KB
- WebP lossy equivalente: ~130KB (28% más pequeño)
- PNG-24: ~2.2MB
- WebP lossless: ~1.6MB (27% más pequeño)

### Soporte de navegadores

A partir de 2023, WebP tiene soporte en el 97%+ de los navegadores en uso activo, incluyendo Chrome, Firefox, Safari (desde 2020), Edge, y Opera. Los únicos navegadores sin soporte nativo son versiones muy antiguas de Internet Explorer y Safari anteriores a 2020.

Para los casos donde necesitas compatibilidad con navegadores antiguos, el elemento HTML \`<picture>\` permite proporcionar WebP con fallback a JPEG:

\`\`\`html
<picture>
  <source srcset="imagen.webp" type="image/webp">
  <img src="imagen.jpg" alt="Descripción">
</picture>
\`\`\`

### Limitaciones de WebP

- **Soporte en software de edición:** Photoshop, Lightroom, GIMP y la mayoría de editores modernos lo soportan, pero el ecosistema es menos universal que JPEG o PNG.
- **Compatibilidad con plataformas antiguas:** Algunas plataformas de e-commerce o CMS con versiones antiguas pueden no gestionar bien WebP.
- **Impresión:** Las herramientas de preprensa e impresión normalmente no trabajan con WebP. Para imprimir, TIFF o PNG son los formatos estándar.

## Cómo decidir: árbol de decisión

**¿La imagen es una fotografía (retrato, paisaje, producto)?**
→ Sí → **WebP** (con fallback JPEG si necesitas compatibilidad máxima)
→ No, continúa

**¿La imagen necesita transparencia?**
→ Sí → **WebP** (o PNG si necesitas compatibilidad con Safari antiguo)
→ No, continúa

**¿La imagen es un logo, icono, o gráfico con pocos colores?**
→ Sí → **PNG-8** o **SVG** (si es vectorial)
→ No, continúa

**¿Es una captura de pantalla o imagen con texto?**
→ Sí → **PNG** (o WebP lossless para web moderna)
→ No → **JPEG** como opción general

## Casos prácticos con números reales

### Foto de producto en e-commerce

Imagen original: foto de zapato, 1200×1200 px, 4.2MB (RAW procesado)

| Formato | Tamaño | Calidad visual |
|---|---|---|
| JPEG 90% | 320KB | Excelente |
| JPEG 80% | 195KB | Muy buena |
| WebP 85% | 215KB | Excelente |
| WebP 75% | 145KB | Muy buena |
| PNG-24 | 3.1MB | Perfecta |

Para e-commerce: WebP 80% es el mejor balance. JPEG 85% si necesitas compatibilidad máxima.

### Logo para web

Logo corporativo, 400×200 px, con fondo transparente y 6 colores planos:

| Formato | Tamaño | Transparencia |
|---|---|---|
| PNG-8 | 8KB | Sí |
| PNG-24 | 22KB | Sí |
| WebP lossless | 15KB | Sí |
| SVG (si es vectorial) | 3KB | Sí |
| JPEG | — | No (no aplica) |

Para logos: PNG-8 o SVG. No usar JPEG (no tiene transparencia y degrada los bordes).

### Captura de pantalla con texto

Captura de interfaz, 1280×800 px, texto negro sobre fondo blanco:

| Formato | Tamaño | Legibilidad texto |
|---|---|---|
| PNG | 280KB | Perfecta |
| WebP lossless | 195KB | Perfecta |
| JPEG 90% | 220KB | Buena |
| JPEG 75% | 140KB | Degradada |

Para capturas: PNG o WebP lossless. JPEG solo si el tamaño es crítico y la legibilidad del texto es secundaria.

## Preguntas frecuentes

### ¿JPEG y JPG son el mismo formato?

Sí. JPG es simplemente la extensión de tres letras de JPEG, herencia del límite de DOS de cuatro caracteres para extensiones (incluyendo el punto). El formato y algoritmo son idénticos. Algunos sistemas crean archivos con extensión .jpeg, pero técnicamente son lo mismo.

### ¿Puedo convertir JPEG a PNG para mejorar la calidad?

No. Convertir un JPEG degradado a PNG no recupera la calidad perdida — solo produce un PNG más grande que guarda exactamente los mismos datos degradados del JPEG. La pérdida de calidad de JPEG es permanente. Para mejorar la calidad, necesitas el archivo original antes de la compresión.

### ¿WebP reemplazará a JPEG y PNG?

Para uso web, WebP ya es la opción preferible en la mayoría de casos. Pero JPEG y PNG seguirán siendo estándares durante muchos años por compatibilidad universal con software de edición, impresión, y sistemas heredados. En la práctica coexisten los tres formatos.

### ¿Cuándo tiene sentido usar AVIF en lugar de WebP?

AVIF (AV1 Image Format, 2019) es incluso más eficiente que WebP — 20-50% más pequeño que WebP con calidad equivalente. Pero el soporte de navegadores es todavía menor (~85%) y la codificación es más lenta. Para web moderna y no crítica en rendimiento, WebP es el balance correcto. AVIF empieza a valer la pena para sitios con miles de imágenes donde el ahorro de ancho de banda es significativo.

### ¿PNG siempre es mejor que JPEG para logos?

Para logos con fondo transparente: sí, PNG es la única opción entre los dos (JPEG no soporta transparencia). Para logos sin transparencia sobre fondo blanco: JPEG puede ser menor en tamaño, pero WebP lossless o PNG-8 suelen producir mejor resultado para gráficos con colores planos.

## Conclusión

La regla práctica: **fotos → WebP (o JPEG como fallback), transparencias → WebP o PNG, gráficos con pocos colores → PNG-8 o SVG**.

Para web moderna sin restricciones de compatibilidad, WebP cubre el 90% de los casos con el mejor balance de calidad y tamaño. Para máxima compatibilidad incluyendo sistemas antiguos, JPEG para fotos y PNG para gráficos con transparencia es la combinación más segura.

Usa nuestro [compresor de imágenes](/) para convertir entre formatos y encontrar el punto óptimo de calidad para cada imagen.
    `
  },
  {
    slug: "optimizar-imagenes-para-seo",
    title: "Cómo optimizar imágenes para SEO: guía técnica completa 2026",
    description: "Guía técnica para optimizar imágenes y mejorar posicionamiento en Google. Alt text, nombres de archivo, formatos, lazy loading, datos estructurados y Core Web Vitals.",
    date: "2026-02-05",
    readTime: "9 min",
    keywords: "optimizar imagenes seo, seo imagenes, alt text imagenes, google images seo, posicionamiento imagenes web",
    content: `
## Por qué las imágenes son críticas para el SEO

Las imágenes son uno de los elementos que más afectan al posicionamiento en Google, por dos razones distintas:

**1. Google Images es un buscador propio.** Representa entre el 20-25% de todas las búsquedas en Google. Una imagen bien optimizada puede atraer tráfico directamente desde Google Images, completamente independiente del posicionamiento de la página.

**2. Las imágenes impactan directamente en Core Web Vitals.** El LCP (Largest Contentful Paint) — la métrica que Google usa para evaluar la velocidad de carga percibida — es frecuentemente una imagen. Una imagen de 3MB puede arruinar tu LCP aunque el resto de la página sea rápido.

Esta guía cubre los dos aspectos: optimización para búsqueda de imágenes y optimización para rendimiento de página.

## Factor 1: Nombres de archivo descriptivos

El nombre del archivo es la primera señal que lee Google para entender qué muestra la imagen. Es un factor de relevancia pequeño pero fácil de implementar.

**Regla:** nombre en minúsculas, palabras separadas por guiones, describe el contenido específico de la imagen.

❌ \`IMG_20240115_123456.jpg\` — sin información
❌ \`foto1.jpg\` — genérico
❌ \`image_final_v2_compressed.jpg\` — metadatos de producción, no descripción
✅ \`compresor-imagenes-jpg-online-gratis.jpg\` — descriptivo y con keywords
✅ \`zapatilla-nike-air-max-blanca-lateral.jpg\` — específico para e-commerce

**Por qué importa:** cuando Google rastrea una página, indexa el nombre del archivo antes de analizar el contenido de la imagen. Un nombre descriptivo mejora la relevancia para búsquedas relacionadas en Google Images.

## Factor 2: Texto alternativo (alt text)

El atributo \`alt\` es el factor más importante para posicionamiento en Google Images. Google usa el alt text como la descripción principal de lo que muestra la imagen.

### Cómo escribir buen alt text

**Principio:** describe lo que hay en la imagen de forma específica, como si se lo explicaras a alguien que no puede verla.

❌ \`alt=""\` — vacío
❌ \`alt="imagen"\` — genérico sin información
❌ \`alt="foto foto foto compresión compresión"\` — keyword stuffing (penaliza)
✅ \`alt="Herramienta online para comprimir imágenes JPG sin perder calidad"\` — descriptivo
✅ \`alt="Comparativa de calidad JPEG al 85% vs 60% en fotografía de retrato"\` — específico

### Cuándo NO usar alt text

Las imágenes puramente decorativas (separadores, fondos, iconos sin información) deben tener \`alt=""\` (vacío, no omitido). Un \`alt=""\` le dice a Google y a los lectores de pantalla que la imagen es decorativa. Omitir el atributo completamente es incorrecto.

### Alt text para imágenes de producto

Para e-commerce, el alt text óptimo incluye: marca + nombre del producto + variante + contexto.

\`alt="Zapatilla Nike Air Max 90 blanca y gris - vista lateral"\` — completo
\`alt="Nike Air Max 90"\` — demasiado breve

## Factor 3: Compresión y peso de archivo

El peso de las imágenes afecta directamente al LCP (Core Web Vitals) y a la velocidad de carga general. Google usa la velocidad de carga como factor de posicionamiento desde 2021.

### Objetivos de peso por tipo de imagen

| Tipo de imagen | Peso objetivo | Dimensiones típicas |
|---|---|---|
| Imagen hero/banner | < 200KB | 1200-1600px ancho |
| Foto de producto (principal) | < 150KB | 800-1000px |
| Foto de producto (miniatura) | < 30KB | 200-400px |
| Imagen de blog | < 120KB | 800-1000px |
| Avatar/perfil | < 20KB | 150-300px |
| OG image (compartir social) | < 200KB | 1200×630px |

### El formato correcto reduce el peso sin perder calidad

- **Fotografías:** WebP (25-35% más pequeño que JPEG con igual calidad) o JPEG 80-85%
- **Logos y gráficos:** PNG-8 o SVG (vectorial cuando sea posible)
- **Capturas de pantalla:** PNG o WebP lossless
- **Imágenes con transparencia:** WebP o PNG

Usa nuestro [compresor de imágenes](/) para encontrar el punto óptimo de calidad y formato para cada imagen.

## Factor 4: Dimensiones correctas

Cargar una imagen de 4000×3000 px cuando se muestra a 800×600 px desperdicia ancho de banda, ralentiza la página, y no mejora la calidad visible.

**Regla:** el ancho máximo de la imagen en el HTML define el ancho máximo que necesitas. Si una imagen se muestra a máximo 800px de ancho en tu diseño, una imagen de 1600px es suficiente (para pantallas de alta densidad 2x). Una imagen de 4000px es exceso puro.

### Imágenes responsive con srcset

Para servir diferentes tamaños según el dispositivo:

\`\`\`html
<img
  src="producto-800.jpg"
  srcset="producto-400.jpg 400w, producto-800.jpg 800w, producto-1200.jpg 1200w"
  sizes="(max-width: 600px) 400px, (max-width: 1200px) 800px, 1200px"
  alt="Descripción del producto"
/>
\`\`\`

Esto sirve la imagen de 400px en móvil y 1200px en escritorio — sin descargar la imagen grande en dispositivos que no la necesitan.

## Factor 5: Lazy loading

Lazy loading retrasa la carga de imágenes que están fuera del viewport (no visibles sin hacer scroll) hasta que el usuario se acerca a ellas. Mejora el LCP porque el navegador prioriza las imágenes visibles.

\`\`\`html
<!-- Imagen fuera del viewport inicial: lazy loading -->
<img src="imagen-abajo.jpg" loading="lazy" alt="Descripción" />

<!-- Imagen hero/LCP: NO usar lazy loading - cargar de inmediato -->
<img src="hero-banner.jpg" loading="eager" alt="Banner principal" />
\`\`\`

**Error común:** aplicar \`loading="lazy"\` a la imagen principal (hero/banner). Eso retrasa la carga del elemento LCP, empeorando exactamente la métrica que intentas mejorar.

## Factor 6: Preload para la imagen LCP

Si tu imagen LCP es una imagen CSS de fondo o una imagen crítica que no está en el HTML inicial, el navegador la descubre tarde. Puedes forzar carga temprana con preload:

\`\`\`html
<link rel="preload" as="image" href="/hero-banner.webp" type="image/webp" />
\`\`\`

Esto le dice al navegador que descargue esa imagen de inmediato, antes de que procese el CSS o el JavaScript que la referencian.

## Factor 7: Definir dimensiones para evitar CLS

El CLS (Cumulative Layout Shift) mide cuánto se "mueve" el contenido de la página durante la carga. Las imágenes sin dimensiones definidas causan layout shifts: el navegador no reserva espacio para ellas, el texto se coloca encima, y cuando la imagen carga, todo el contenido "salta" hacia abajo.

**Solución:** siempre define \`width\` y \`height\` en las imágenes:

\`\`\`html
<img src="foto.jpg" width="800" height="600" alt="Descripción" />
\`\`\`

O usando CSS con aspect-ratio:

\`\`\`css
img {
  aspect-ratio: 4 / 3;
  width: 100%;
}
\`\`\`

Ambas técnicas le dicen al navegador el espacio que debe reservar antes de que la imagen cargue, eliminando el layout shift.

## Factor 8: Datos estructurados para imágenes

Los datos estructurados permiten que tus imágenes aparezcan en resultados enriquecidos de Google, incluyendo el carrusel de imágenes en la búsqueda principal.

### Imágenes en Article schema

Para posts de blog con imagen destacada:

\`\`\`json
{
  "@type": "Article",
  "image": {
    "@type": "ImageObject",
    "url": "https://ejemplo.com/imagen-articulo.jpg",
    "width": 1200,
    "height": 630
  }
}
\`\`\`

### Imágenes en Product schema

Para e-commerce, las imágenes en el schema de producto pueden aparecer en Google Shopping:

\`\`\`json
{
  "@type": "Product",
  "image": [
    "https://ejemplo.com/producto-frontal.jpg",
    "https://ejemplo.com/producto-lateral.jpg"
  ]
}
\`\`\`

## Factor 9: Sitemap de imágenes

Un sitemap de imágenes ayuda a Google a descubrir imágenes que no aparecen directamente en el HTML o que están cargadas por JavaScript. Se añade al sitemap XML existente:

\`\`\`xml
<url>
  <loc>https://ejemplo.com/pagina-producto/</loc>
  <image:image>
    <image:loc>https://ejemplo.com/imagenes/producto-123.jpg</image:loc>
    <image:caption>Nombre del producto - descripción</image:caption>
    <image:title>Nombre del producto</image:title>
  </image:image>
</url>
\`\`\`

Para sitios con muchas imágenes dinámicas, el sitemap de imágenes puede ser un factor significativo en la indexación.

## Checklist completo de optimización SEO de imágenes

- Nombres de archivo descriptivos con keywords, separados por guiones
- Alt text específico y descriptivo en todas las imágenes de contenido
- \`alt=""\` en imágenes puramente decorativas
- Peso dentro de los objetivos por tipo (hero < 200KB, producto < 150KB)
- Formato correcto: WebP o JPEG para fotos, PNG para gráficos/transparencias
- Dimensiones definidas (width + height) en todas las imágenes para evitar CLS
- \`loading="lazy"\` en imágenes fuera del viewport inicial
- Sin lazy loading en la imagen LCP
- Preload para la imagen LCP si no está en el HTML inicial
- Imágenes responsive con srcset para diferentes tamaños de pantalla
- Datos estructurados con ImageObject cuando aplica (artículos, productos)

## Herramientas para medir el impacto

**Google PageSpeed Insights** (pagespeed.web.dev): analiza LCP, CLS, y oportunidades de optimización de imágenes. Muestra exactamente qué imágenes están ralentizando tu página y cuánto ahorrarías comprimiéndolas.

**Google Search Console → Experiencia de página**: muestra métricas Core Web Vitals reales de los usuarios de tu sitio.

**Chrome DevTools → Lighthouse**: auditoría completa incluyendo imágenes sin dimensiones, imágenes fuera de tamaño, y oportunidades de lazy loading.

## Preguntas frecuentes

### ¿Cuánto peso puede mejorar mi posicionamiento?

No hay un umbral específico donde "pases" de penalizado a no penalizado. Es una señal continua: mejor LCP → mejor experiencia de página → ventaja en igualdad de otros factores. El impacto es especialmente notable en sectores competitivos donde otras señales de relevancia son similares entre competidores.

### ¿Debo añadir keywords a todos los alt text?

Solo cuando describe genuinamente la imagen. Google penaliza el keyword stuffing en alt text. Si la imagen muestra un proceso de compresión de imágenes, el alt text naturalmente contiene la keyword. Si la imagen es un icono decorativo, no tiene sentido forzar la keyword — usa \`alt=""\`.

### ¿WebP mejora el posicionamiento directamente?

No directamente por ser WebP. Lo que mejora el posicionamiento es la velocidad de carga (LCP), y WebP reduce el peso de la imagen en 25-35%, lo que ayuda al LCP. Es una mejora indirecta pero real.

### ¿Qué pasa si mis imágenes están en un CDN?

El CDN no afecta negativamente al SEO — al contrario, un CDN puede mejorar los tiempos de entrega (Time to First Byte) y el LCP para usuarios geográficamente alejados del servidor de origen. Lo único que debes asegurar es que las URLs de las imágenes en el CDN sean consistentes (no cambien) para que Google las indexe correctamente.

## Conclusión

La optimización SEO de imágenes tiene dos capas: la capa de descubrimiento (nombres de archivo, alt text, datos estructurados, sitemap) que determina si Google puede entender e indexar tus imágenes; y la capa de rendimiento (compresión, formatos, lazy loading, dimensiones) que determina cómo las imágenes afectan a tus Core Web Vitals.

Las dos capas se complementan. Una imagen bien comprimida y bien etiquetada es la combinación ideal.

Para comprimir tus imágenes al formato y calidad óptimos antes de subirlas, usa nuestro [compresor de imágenes](/).
    `
  },
  {
    slug: "como-comprimir-imagenes-para-instagram",
    title: "Cómo comprimir imágenes para Instagram: dimensiones, calidad y proceso completo",
    description: "Guía técnica para preparar y comprimir imágenes para Instagram. Dimensiones exactas por formato, por qué Instagram degrada tus fotos, y cómo minimizar la pérdida de calidad.",
    date: "2026-01-28",
    readTime: "9 min",
    keywords: "comprimir imagenes instagram, tamaño fotos instagram, optimizar fotos instagram, calidad instagram, dimensiones instagram 2026",
    content: `
## Por qué Instagram degrada la calidad de tus imágenes

Cuando subes una imagen a Instagram, la plataforma aplica su propio proceso de compresión automáticamente. Este proceso es inevitable, pero entender cómo funciona permite minimizar la pérdida de calidad.

Instagram ejecuta dos operaciones en ese orden:
1. **Redimensionado:** si tu imagen es más grande que las dimensiones máximas del formato, la reduce
2. **Compresión JPEG:** aplica compresión equivalente aproximadamente al 70-75% de calidad

El resultado final depende de cómo le entregues la imagen. Si subes una foto de 4MB sin preparar, Instagram la redimensiona y comprime. Si subes la misma foto ya preparada al tamaño correcto con buena calidad, Instagram solo aplica la compresión — un paso menos significa menos degradación acumulada.

## Dimensiones exactas por formato (2024-2026)

### Feed de Instagram

| Formato | Resolución óptima | Proporción | Mínimo aceptado |
|---|---|---|---|
| Cuadrado | 1080×1080 px | 1:1 | 612×612 px |
| Horizontal | 1080×566 px | 1.91:1 | 612×320 px |
| Vertical (retrato) | 1080×1350 px | 4:5 | 612×766 px |

**Nota sobre la proporción vertical 4:5:** ocupa más espacio en el feed del usuario que el cuadrado, lo que puede traducirse en mayor engagement visual. Para fotografía de personas es el formato más efectivo.

### Stories e Instagram Reels

- **Resolución óptima:** 1080×1920 px
- **Proporción:** 9:16
- **Zona segura de texto:** evita los 250px superiores e inferiores — la interfaz de Instagram (nombre de usuario, botones) puede solapar ese área

Para Stories con texto o elementos importantes, trabaja dentro de la zona segura central: 1080×1420px del total de 1920px de alto.

### Foto de perfil

- **Resolución mínima al subir:** 320×320 px
- **Resolución recomendada:** 1080×1080 px
- **Cómo se muestra:** recortada en círculo a ~110px en el feed

Instagram muestra la foto de perfil pequeña, pero la guarda a mayor resolución para pantallas de alta densidad. Sube al menos 400×400px para que se vea nítida en iPhone y Android de gama alta.

## Por qué importa la resolución de subida

Existe un error común: pensar que subir más resolución de la que Instagram muestra no tiene efecto. En realidad sí importa, hasta cierto punto.

Si subes 612×612 px (el mínimo aceptado), Instagram muestra la imagen a esa resolución — se verá borrosa en pantallas de alta densidad (los iPhone y Android modernos tienen pantallas de 2x o 3x de densidad).

Si subes 1080×1080 px, Instagram puede servir la imagen más nítida en pantallas de alta densidad.

Si subes 2160×2160 px (el doble de 1080), Instagram lo reduce a 1080 — ninguna ventaja.

**El punto óptimo es 1080px en el lado más largo para feed**, ni más ni menos.

## Perfil de color: por qué los colores cambian en Instagram

Si tus fotos se ven con colores diferentes (más saturados o más apagados) en Instagram vs tu editor, el problema es el perfil de color.

Instagram convierte todas las imágenes a sRGB al procesarlas. Si tu imagen está en AdobeRGB o ProPhoto RGB (common en cámaras profesionales y Lightroom), los colores se "traducen" a sRGB durante ese proceso y el resultado puede verse diferente.

**Solución:** antes de subir, convierte el perfil de color a sRGB.

- En Lightroom: Exportar → Espacio de color → sRGB
- En Photoshop: Archivo → Guardar para web → Perfil: sRGB marcado
- Nuestro compresor exporta siempre en sRGB

## Proceso de preparación: paso a paso

### Paso 1: Edita la foto en tu editor preferido

Lightroom, Snapseed, VSCO, o el editor que uses. Termina toda la edición antes de exportar para Instagram. No apliques filtros de Instagram encima de una foto ya editada — suma otra ronda de procesamiento.

### Paso 2: Exporta con las dimensiones correctas

Exporta al tamaño final de Instagram:
- Feed: 1080px en el lado más largo
- Stories/Reels: 1080×1920px
- Perfil: 1080×1080px

### Paso 3: Elige la calidad de exportación correcta

La paradoja del JPEG de Instagram: si comprimes al 70-75% antes de subir (para ahorrar espacio), Instagram aplica su propia compresión del 70-75% sobre ese archivo ya comprimido. El resultado final tiene doble compresión y artefactos acumulados.

**La regla correcta:** sube con calidad 85-90% JPEG. Dejas que Instagram aplique su compresión sobre un archivo de alta calidad, no sobre uno ya degradado.

Si usas nuestro [compresor de imágenes](/):
- Selecciona calidad 85-90%
- Asegúrate de que las dimensiones son las correctas para el formato
- El peso del archivo para una foto de feed típica será 500KB-1.5MB — perfecto

### Paso 4: Sube desde la app a la resolución correcta

Guarda la imagen exportada en tu galería y súbela desde Instagram, seleccionando "Publicidad" → elige la foto de tu galería. No uses la cámara de Instagram para fotos importantes — pasa por compresión adicional.

## Imágenes con texto superpuesto: requieren más calidad

Las imágenes con texto (diseños de infografías, quotes, anuncios) son especialmente vulnerables a la compresión de Instagram. El texto superpuesto crea zonas de alto contraste en bordes definidos — exactamente el tipo de contenido que JPEG comprime peor.

Para estas imágenes:
- Exporta a calidad JPEG 90-95%
- Asegúrate de que el texto está renderizado a la resolución final antes de exportar (no apliques texto en Instagram)
- Considera PNG para diseños con texto sobre fondo sólido — aunque Instagram lo convierte a JPEG internamente, el archivo de partida tiene calidad perfecta

## Diferencias entre iOS y Android

La app de Instagram en iOS y Android puede producir resultados ligeramente diferentes al subir:

- **iOS:** tiende a preservar algo mejor la calidad en fotografías, especialmente en contenido HDR
- **Android:** puede haber más variabilidad según fabricante y versión del sistema

Para resultados más consistentes: guarda la foto editada en la galería y súbela desde la librería, en lugar de usar la cámara integrada de Instagram.

## El formato PNG en Instagram

Instagram acepta PNG pero lo convierte a JPEG internamente al procesar. Subir un PNG no te da mejor calidad final — Instagram aplica su compresión JPEG de todas formas. La única razón para subir PNG es si el archivo PNG de origen es de mayor calidad que el JPEG que generarías tú, lo que raramente ocurre si controlas el proceso de exportación.

**Recomendación:** JPEG 85-90% para todas las subidas a Instagram, excepto si necesitas compatibilidad con un flujo específico.

## Errores frecuentes

### Comprimir demasiado antes de subir

Comprimir al 60-70% para reducir el tamaño del archivo antes de subir resulta en doble compresión: tu 70% + el 70% de Instagram = resultado notablemente degradado. Sube a 85-90% y deja que Instagram aplique su compresión sobre un buen archivo.

### Subir directamente desde la cámara del teléfono

Las fotos de un iPhone moderno tienen 3024×4032 px y pesan 3-5MB. Instagram las redimensiona y comprime. Preparar la imagen primero (resize + compresión controlada) produce mejor resultado final que dejar todo a Instagram.

### Usar filtros de Instagram sobre fotos ya editadas

Si editaste la foto en Lightroom y luego aplicas un filtro en Instagram, el filtro se aplica sobre la foto ya procesada y luego Instagram comprime el resultado. Para evitar doble procesamiento: haz toda la edición externamente y sube sin filtros de Instagram.

### No respetar la zona segura en Stories

En Stories, las zonas superior e inferior tienen elementos de interfaz (nombre de cuenta, barra de progreso, botones de respuesta) que cubren la imagen. Texto o elementos importantes en esa zona quedan ocultos.

## Herramientas recomendadas

**Para editar en móvil:**
- Lightroom Mobile (iOS/Android, gratuito con limitaciones): el mejor editor fotográfico móvil, exporta en sRGB
- Snapseed (gratuito): edición rápida y potente, buen para ajustes de nitidez y color

**Para preparar antes de subir:**
- Nuestro [compresor de imágenes](/) para redimensionar al tamaño exacto de Instagram y comprimir al nivel correcto
- Crop & Resize en Lightroom (versión desktop para flujos profesionales)

## Preguntas frecuentes

### ¿Puedo subir imágenes de más de 1080px a Instagram?

Sí. Instagram acepta hasta 2048px. Los reduce internamente a 1080px. No hay ventaja en subir más de 1080px — la imagen final será la misma.

### ¿El tipo de cuenta (personal vs business vs creator) afecta la compresión?

No. El tipo de cuenta no afecta cómo Instagram procesa las imágenes. La compresión es igual para todos.

### ¿Las imágenes pierden calidad con el tiempo en Instagram?

No. Una vez subida, Instagram no aplica compresión adicional con el tiempo. La imagen base permanece igual. Lo que puede variar es cómo la sirve según la conexión del usuario, pero el archivo almacenado no cambia.

### ¿Por qué mis fotos se ven mejor en la app que cuando las descargo?

Porque Instagram aplica su propio renderizado optimizado al mostrar imágenes. El archivo descargado es la imagen JPEG comprimida tal cual. La app aplica procesamiento de pantalla adicional. Lo que ven tus seguidores en la app generalmente se ve mejor que el archivo raw descargado.

### ¿Cuánto espacio ocupan las imágenes de Instagram en el servidor?

No es información pública. Desde tu perspectiva como usuario: subes un archivo, Instagram lo almacena en una versión comprimida. El espacio en tu dispositivo no se usa (Instagram no descarga las imágenes del feed en el almacenamiento interno).

## Conclusión

Para obtener la mejor calidad en Instagram: usa la resolución correcta para el formato (1080px de ancho para feed, 1080×1920 para Stories), exporta en sRGB, y sube con calidad JPEG 85-90%.

Lo más importante es no comprimir agresivamente antes de subir. Instagram aplicará su propia compresión — controla la calidad del archivo de entrada y el resultado final será notablemente mejor.

Para preparar tus imágenes antes de subirlas a Instagram, usa nuestro [compresor de imágenes](/).
    `
  },
  {
    slug: "guia-core-web-vitals-imagenes",
    title: "Core Web Vitals e imágenes: cómo LCP, CLS e INP dependen de tu optimización",
    description: "Guía técnica sobre el impacto de las imágenes en Core Web Vitals. Cómo mejorar LCP, eliminar CLS causado por imágenes y optimizar INP con técnicas específicas y ejemplos de código.",
    date: "2026-01-20",
    readTime: "10 min",
    keywords: "core web vitals imagenes, lcp optimizar imagenes, cls imagenes, rendimiento web imagenes, google page speed imagenes",
    content: `
## Qué son Core Web Vitals y por qué Google los usa para posicionar

Core Web Vitals son tres métricas que Google usa para medir la experiencia real del usuario en una página web. Desde 2021, forman parte del algoritmo de posicionamiento de Google como "señal de experiencia de página".

Las tres métricas:

**LCP — Largest Contentful Paint:** tiempo desde que el usuario inicia la navegación hasta que el elemento más grande visible en la pantalla ha terminado de renderizar. Mide la velocidad de carga percibida. En la mayoría de páginas web, ese elemento más grande es una imagen.

**CLS — Cumulative Layout Shift:** suma de todos los movimientos inesperados del contenido durante la carga. Mide la estabilidad visual. Las imágenes sin dimensiones definidas son la causa más común de CLS alto.

**INP — Interaction to Next Paint:** tiempo de respuesta desde que el usuario interactúa (clic, toque) hasta que la página responde visualmente. Reemplazó a FID en marzo 2024.

Los umbrales que Google considera "buenos":
- LCP: menos de 2.5 segundos
- CLS: menos de 0.1
- INP: menos de 200 milisegundos

Las imágenes afectan directamente a LCP y CLS, e indirectamente a INP.

## LCP: las imágenes son el factor más común

En el 70-80% de las páginas web, el elemento LCP es una imagen. Los tipos más habituales:
- Imagen hero o banner en la parte superior de la página
- Imagen de producto en páginas de e-commerce
- Imagen destacada en artículos de blog

### Por qué una imagen puede arruinar tu LCP

Si tu imagen hero pesa 2MB, el navegador necesita:
1. Descubrirla en el HTML o CSS
2. Iniciar la descarga
3. Descargar los 2MB (en una conexión de 10Mbps tarda ~1.6 segundos solo la descarga)
4. Decodificarla
5. Pintarla en pantalla

En una conexión móvil media (5-10 Mbps), ese proceso puede superar fácilmente 3-4 segundos. LCP de 4 segundos = "necesita mejora" según Google.

### Solución 1: Comprimir la imagen LCP

El objetivo para la imagen LCP es estar por debajo de 200KB. Para una imagen de 1200-1600px de ancho con contenido fotográfico, eso se consigue con JPEG 80-85% o WebP equivalente.

Comparativa para una imagen hero de 1400px de ancho:
- Original (sin comprimir): 2.1MB → LCP estimado en 3G: 4-6 segundos
- JPEG 85%: 180KB → LCP estimado en 3G: <1.5 segundos
- WebP 80%: 130KB → LCP estimado en 3G: <1 segundo

### Solución 2: Preload para carga temprana

Por defecto, el navegador descubre las imágenes cuando analiza el HTML. Si la imagen está definida en CSS como fondo, o si está en un componente JavaScript que carga tarde, el navegador la descubre aún más tarde.

Para la imagen LCP, añade un \`<link rel="preload">\` en el \`<head>\`:

\`\`\`html
<link
  rel="preload"
  as="image"
  href="/hero-banner.webp"
  type="image/webp"
  fetchpriority="high"
/>
\`\`\`

El atributo \`fetchpriority="high"\` (disponible en Chrome desde 2022, Firefox desde 2023) le dice explícitamente al navegador que esta imagen tiene prioridad máxima.

**Cuándo usar preload:** solo para la imagen LCP. Hacer preload de muchas imágenes tiene el efecto contrario — satura el ancho de banda y puede retrasar la imagen LCP.

### Solución 3: No usar lazy loading en la imagen LCP

El atributo \`loading="lazy"\` retrasa la carga de imágenes hasta que el usuario se acerca a ellas. Es excelente para imágenes fuera del viewport inicial, pero es un error grave en la imagen LCP:

\`\`\`html
<!-- ❌ Error: lazy loading en la imagen LCP -->
<img src="hero-banner.jpg" loading="lazy" alt="Banner" />

<!-- ✅ Correcto: sin lazy loading (o con eager) en la imagen LCP -->
<img src="hero-banner.jpg" loading="eager" alt="Banner" fetchpriority="high" />
\`\`\`

### Solución 4: Formatos modernos

WebP reduce el peso de las imágenes entre un 25-35% comparado con JPEG a igual calidad visual. Para la imagen LCP, ese ahorro se traduce directamente en velocidad de carga.

Para máxima compatibilidad (incluyendo Safari antiguo), usa el elemento \`<picture>\`:

\`\`\`html
<picture>
  <source srcset="hero.webp" type="image/webp" />
  <img src="hero.jpg" alt="Banner principal" width="1400" height="600" fetchpriority="high" />
</picture>
\`\`\`

## CLS: las imágenes sin dimensiones causan layout shifts

El CLS ocurre cuando el contenido de la página se mueve después de haberse mostrado inicialmente. Las imágenes sin dimensiones definidas son la causa número uno de CLS alto.

### Por qué ocurre el problema

Cuando el navegador carga el HTML, reserva espacio para cada elemento según lo que sabe de él. Si una imagen no tiene \`width\` y \`height\` definidos, el navegador no sabe cuánto espacio reservar — lo pone a cero. Cuando la imagen carga, ocupa su espacio real y empuja todo el contenido que había debajo.

Ejemplo:
1. Página carga → texto visible debajo de un espacio en blanco de 0px
2. Imagen carga (tarda 1 segundo) → imagen ocupa 400px de alto
3. Todo el texto "salta" 400px hacia abajo → CLS alto

### Solución: siempre define width y height

\`\`\`html
<!-- ❌ Sin dimensiones → CLS -->
<img src="producto.jpg" alt="Producto" />

<!-- ✅ Con dimensiones → sin CLS -->
<img src="producto.jpg" alt="Producto" width="800" height="600" />
\`\`\`

El navegador no escala la imagen al tamaño indicado automáticamente — CSS controla el tamaño visual. Lo que hacen \`width\` y \`height\` en HTML es darle al navegador la proporción de la imagen para que pueda reservar el espacio correcto antes de que la imagen cargue.

### Solución con CSS y aspect-ratio

Para imágenes con diseño responsive (que cambian de tamaño con la pantalla), la técnica moderna es usar \`aspect-ratio\` en CSS:

\`\`\`css
img {
  width: 100%;
  height: auto;
  aspect-ratio: 4 / 3;
}
\`\`\`

O combinando HTML y CSS:

\`\`\`html
<img src="producto.jpg" alt="Producto" width="800" height="600"
     style="width: 100%; height: auto;" />
\`\`\`

Los valores \`width="800" height="600"\` en HTML establecen la proporción 4:3. CSS con \`height: auto\` hace la imagen responsive. El navegador reserva el espacio correcto en la proporción 4:3.

### Identificar imágenes causando CLS

En Chrome DevTools:
1. Abre DevTools → pestaña Performance
2. Graba la carga de la página
3. En el panel de resultados, busca "Layout Shift" (aparece en rojo)
4. Click en el evento → verás qué elemento causó el shift

En Google Search Console → Core Web Vitals → páginas con CLS "necesita mejora" → analiza qué elementos se mueven.

## INP: imágenes y el hilo principal

INP mide el tiempo de respuesta a interacciones. Las imágenes no afectan directamente a INP, pero pueden contribuir indirectamente de dos formas:

**Decodificación de imágenes bloqueante:** cuando el navegador decodifica una imagen JPEG o PNG, usa el hilo principal del navegador. Si decodifica una imagen grande en el momento en que el usuario interactúa, la interacción se retrasa.

**Solución:** usa \`decoding="async"\` para que la decodificación ocurra en un hilo separado:

\`\`\`html
<img src="imagen-grande.jpg" decoding="async" alt="Descripción" />
\`\`\`

**Muchas imágenes cargando simultáneamente:** en páginas con muchas imágenes (galerías, tiendas), cargar todas al mismo tiempo puede saturar el ancho de banda y ralentizar la respuesta a interacciones.

**Solución:** lazy loading para todas las imágenes fuera del viewport inicial.

## Lazy loading: cuándo y cómo usarlo correctamente

Lazy loading retrasa la carga de imágenes hasta que el usuario se acerca a ellas. Es una de las optimizaciones más efectivas para páginas con muchas imágenes.

\`\`\`html
<!-- Imágenes fuera del viewport inicial: lazy loading -->
<img src="galeria-1.jpg" loading="lazy" alt="Imagen 1" width="400" height="300" />
<img src="galeria-2.jpg" loading="lazy" alt="Imagen 2" width="400" height="300" />

<!-- Imagen LCP (primera imagen visible): NO lazy loading -->
<img src="hero.jpg" loading="eager" alt="Hero" width="1400" height="600" />
\`\`\`

**Regla de oro:** la imagen LCP nunca debe tener \`loading="lazy"\`. Todas las demás imágenes que no son visibles en el viewport inicial deberían tenerlo.

## Imágenes responsive con srcset

Servir una imagen de 1400px a un móvil que la muestra a 375px descarga 10x más datos de los necesarios.

\`\`\`html
<img
  src="producto-800.jpg"
  srcset="
    producto-400.jpg 400w,
    producto-800.jpg 800w,
    producto-1200.jpg 1200w
  "
  sizes="(max-width: 400px) 400px, (max-width: 800px) 800px, 1200px"
  alt="Producto"
  width="800"
  height="600"
  loading="lazy"
/>
\`\`\`

El atributo \`sizes\` le dice al navegador cuántos píxeles CSS va a ocupar la imagen en cada breakpoint. El navegador elige la versión más apropiada del \`srcset\`. Para un iPhone con pantalla de 390px a densidad 2x, elegiría la imagen de 800px (390 × 2 = 780, aproximado a 800w).

## Tabla de impacto por tipo de optimización

| Optimización | LCP | CLS | INP | Esfuerzo |
|---|---|---|---|---|
| Comprimir imagen LCP | Alto | — | — | Bajo |
| Preload imagen LCP | Alto | — | — | Bajo |
| Eliminar lazy loading en LCP | Alto | — | — | Bajo |
| Definir width/height en imágenes | — | Alto | — | Bajo |
| Lazy loading en imágenes below fold | Medio | — | Bajo | Bajo |
| Imágenes responsive (srcset) | Medio | — | Bajo | Medio |
| Convertir a WebP | Medio | — | — | Medio |
| decoding="async" | — | — | Bajo | Bajo |

## Cómo medir tus Core Web Vitals actuales

**Google PageSpeed Insights** (pagespeed.web.dev): introduce la URL de tu página y obtienes las métricas reales (basadas en datos de Chrome UX Report) y de laboratorio, con recomendaciones específicas.

**Google Search Console → Core Web Vitals**: muestra datos reales agregados de todos los usuarios de tu sitio, agrupados por "buenas", "necesita mejora" y "deficientes". Acceso gratuito con Google Search Console.

**Chrome DevTools → Lighthouse**: auditoría local desde tu ordenador. Útil para desarrollo, pero los datos de laboratorio difieren de los datos reales de campo.

**Chrome Extension Web Vitals**: extensión oficial de Google que muestra LCP, CLS e INP en tiempo real mientras navegas.

## Preguntas frecuentes

### ¿Cuánto mejora el posicionamiento optimizar Core Web Vitals?

No hay un porcentaje publicado por Google. La señal de experiencia de página es un desempate: cuando dos páginas tienen relevancia similar para una búsqueda, la que tiene mejores Core Web Vitals puede posicionarse mejor. En nichos muy competitivos con mucho contenido equivalente, puede ser la diferencia entre la primera y la segunda página. En nichos menos competitivos, el contenido y los backlinks siguen siendo más determinantes.

### ¿Las imágenes afectan a móvil y escritorio por igual?

El impacto en LCP es mayor en móvil porque las conexiones son más lentas y el ancho de banda más limitado. Google mide los Core Web Vitals separadamente para móvil y escritorio en Search Console. En la mayoría de sitios, los Core Web Vitals de móvil son peores, y es en móvil donde hay más margen de mejora.

### ¿Cuánto tiempo tarda en verse el impacto en posicionamiento?

Google actualiza los datos de Core Web Vitals en Search Console mensualmente (con datos de los últimos 28 días). Los cambios en posicionamiento basados en la señal de experiencia de página suelen reflejarse en 1-3 meses después de que los datos de campo reflejen la mejora.

### ¿El CDN mejora el LCP?

Sí, significativamente. Un CDN acorta la distancia física entre el servidor y el usuario, reduciendo la latencia de red. Para imágenes estáticas, un CDN puede reducir el TTFB (Time to First Byte) en 100-300ms, lo que se traduce directamente en mejor LCP para usuarios geográficamente alejados del servidor de origen.

## Conclusión

Las imágenes son el factor que más influye en LCP y CLS — las dos métricas de Core Web Vitals que más fácilmente se pueden mejorar. Las acciones con mayor impacto por menor esfuerzo son: comprimir la imagen LCP a menos de 200KB, añadir preload para ella, y definir width y height en todas las imágenes del HTML.

Estas cuatro acciones pueden transformar un LCP de 4 segundos en uno de 1.5 segundos y eliminar completamente el CLS causado por imágenes, sin cambiar nada del diseño ni del contenido de la página.

Para comprimir tus imágenes y reducir el peso de la imagen LCP, usa nuestro [compresor de imágenes](/).
    `
  }
];
